import {
  apiErrorMessage,
  fetchQuestionnaireInsights,
  fetchResponseFilterOptions,
  refreshQuestionnaireInsights
} from '@api/taptotip/questionnaire';
import { formatDayFirst } from '@utils/dayFirstDates';
import PropTypes from 'prop-types';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { CARD } from './Editor';
import AnswerBreakdown from './AnswerBreakdown';
import Stars from './Stars';

/**
 * Card 338 — John (CEO): head office's "own personal PIP, based on the
 * questions they ask".
 *
 * Read top down the way a head of operations would: one sentence, then what
 * to do, then why (themes in the guests' own words), then the numbers. The
 * numbers are exact and computed by the backend; only the wording of the
 * themes and actions comes from the AI, and the page says so.
 */

export const PERIODS = [
  { value: 30, label: 'Last 30 days' },
  { value: 90, label: 'Last 90 days' },
  { value: 365, label: 'Last 12 months' }
];

const KIND_LABELS = {
  hotel: 'Hotel',
  experience: 'Experience',
  guide: 'Tour Manager',
  tour: 'Tour',
  question: 'Question'
};

const SENTIMENT_DOT = {
  negative: 'bg-rose-500',
  positive: 'bg-emerald-500',
  mixed: 'bg-amber-400'
};

const SKIPPED = {
  too_few:
    'Themes and actions need at least 5 answers in this period. The numbers below are exact.',
  no_comments:
    'No guest has left a comment in this period yet, so there is nothing to draw themes from.',
  no_ai: 'The written summary is not available on this server. The numbers below are exact.',
  failed:
    'The written summary could not be made this time. Try Refresh later; the numbers below are exact.'
};

const plural = (n, one, many) => `${n} ${n === 1 ? one : many}`;

const timeOf = value =>
  formatDayFirst(value, { day: 'numeric', month: 'short', hour: 'numeric', minute: '2-digit' });

export const scoreLine = line =>
  `${line.average.toFixed(1)}/5 from ${plural(line.count, 'rating', 'ratings')}`;

const Evidence = ({ action, drill }) => (
  <div className="flex flex-wrap gap-2 mt-3">
    {action.commentCount > 0 && (
      <DrillChip
        onClick={
          drill &&
          action.responseIds &&
          (() => drill.ids(action.responseIds, `comments behind “${action.title}”`))
        }>
        {plural(action.commentCount, 'comment', 'comments')}
      </DrillChip>
    )}
    {action.evidence.map(e => (
      <span
        key={`${e.kind}-${e.name}`}
        className="text-xs font-bold rounded-lg px-2 py-1 bg-slate-100 text-slate-700">
        {e.name} · {scoreLine(e)}
      </span>
    ))}
  </div>
);

Evidence.propTypes = { action: PropTypes.object.isRequired, drill: PropTypes.object };

const ScoreList = ({ title, lines, empty, tone, testId }) => (
  <section className={CARD} data-testid={testId}>
    <h3 className="text-base font-black text-slate-900 m-0 mb-3">{title}</h3>
    {lines.length === 0 ? (
      <p className="text-sm text-slate-500 m-0">{empty}</p>
    ) : (
      <ul className="list-none m-0 p-0 flex flex-col divide-y divide-slate-100">
        {lines.map(line => (
          <li key={line.ref} className="py-2 flex items-baseline justify-between gap-3">
            <div className="min-w-0">
              <div className="text-sm font-bold text-slate-900">{line.name}</div>
              <div className="text-xs text-slate-500">
                {KIND_LABELS[line.kind]}
                {line.detail ? `, ${line.detail}` : ''}
              </div>
            </div>
            <div className="text-right whitespace-nowrap">
              <div className={`text-sm font-black tabular-nums ${tone}`}>
                {line.average.toFixed(1)}
              </div>
              <div className="text-xs text-slate-500 tabular-nums">
                {plural(line.count, 'rating', 'ratings')}
              </div>
            </div>
          </li>
        ))}
      </ul>
    )}
  </section>
);

ScoreList.propTypes = {
  title: PropTypes.string,
  lines: PropTypes.array,
  empty: PropTypes.string,
  tone: PropTypes.string,
  testId: PropTypes.string
};

const score = avg => (avg ? avg.average.toFixed(1) : '-');
const percent = share =>
  share === null || share === undefined ? '-' : `${Math.round(share * 100)}%`;

/** Score colour: the same three bands everywhere on the page. */
const band = value =>
  value === null || value === undefined
    ? 'slate'
    : value >= 4.2
    ? 'emerald'
    : value >= 3.6
    ? 'amber'
    : 'rose';
const BAR = {
  emerald: 'bg-emerald-500',
  amber: 'bg-amber-400',
  rose: 'bg-rose-500',
  slate: 'bg-slate-300'
};
const TEXT = {
  emerald: 'text-emerald-700',
  amber: 'text-amber-700',
  rose: 'text-rose-700',
  slate: 'text-slate-500'
};
const PILL = {
  emerald: 'bg-emerald-50 text-emerald-700 hover:bg-emerald-100',
  amber: 'bg-amber-50 text-amber-800 hover:bg-amber-100',
  rose: 'bg-rose-50 text-rose-700 hover:bg-rose-100',
  slate: 'bg-slate-100 text-slate-700 hover:bg-slate-200'
};
/** Unhappy share: under 10% is fine, 10-25% worth a look, above that a problem. */
const unhappyBand = share => (share < 0.1 ? 'emerald' : share <= 0.25 ? 'amber' : 'rose');

/** A labelled score out of 5 with a bar, the page's one way of showing a rating. */
export const ScoreBar = ({ label, avg, testId }) => {
  const value = avg ? avg.average : null;
  return (
    <div className="flex items-center gap-3" data-testid={testId}>
      <span className="w-28 flex-none text-xs font-bold text-slate-500">{label}</span>
      <span className="flex-1 h-2 rounded-full bg-slate-100 overflow-hidden" aria-hidden="true">
        <span
          className={`block h-full rounded-full ${BAR[band(value)]}`}
          style={{ width: value === null ? 0 : `${(value / 5) * 100}%` }}
        />
      </span>
      <span className={`w-9 text-right text-sm font-black tabular-nums ${TEXT[band(value)]}`}>
        {value === null ? '-' : value.toFixed(1)}
      </span>
    </div>
  );
};

ScoreBar.propTypes = { label: PropTypes.string, avg: PropTypes.object, testId: PropTypes.string };

/** A count you can open: takes you to Responses showing exactly those answers. */
const DrillChip = ({ children, onClick, tone = 'slate', testId, title }) =>
  onClick ? (
    <button
      type="button"
      onClick={onClick}
      title={title || 'Show these responses'}
      data-testid={testId}
      className={`text-xs font-bold rounded-lg px-2 py-1 border-0 cursor-pointer transition-colors ${
        PILL[tone]
      }`}>
      {children} <span aria-hidden="true">&rsaquo;</span>
    </button>
  ) : (
    <span className={`text-xs font-bold rounded-lg px-2 py-1 ${PILL[tone]}`} data-testid={testId}>
      {children}
    </span>
  );

DrillChip.propTypes = {
  children: PropTypes.node,
  onClick: PropTypes.func,
  tone: PropTypes.string,
  testId: PropTypes.string,
  title: PropTypes.string
};

/**
 * Andy, 2026-10-02: "any feedback on the guide is key". One row per Tour
 * Manager, concerns first: what guests said on the left, the numbers as bars
 * on the right, and every count opens the answers behind it.
 */
const TourManagers = ({ guides, feedback, drill, scopeLabel }) => {
  if (!guides || guides.length === 0) return null;
  const said = new Map((feedback || []).map(f => [f.ref, f]));
  const rows = [...guides].sort((a, b) => {
    const fa = said.get(a.ref);
    const fb = said.get(b.ref);
    return (fb ? fb.concerns : 0) - (fa ? fa.concerns : 0) || a.averageOverall - b.averageOverall;
  });
  return (
    <section className={CARD} data-testid="insights-guides">
      <h2 className="text-base font-black text-slate-900 m-0 mb-1">Tour Managers</h2>
      <p className="text-xs text-slate-500 m-0 mb-3">
        Rated on {scopeLabel || 'the answers shown'}: each Tour Manager&rsquo;s results, and what
        guests said about them as a person, from their own departures.
      </p>
      <ul className="list-none m-0 p-0 flex flex-col divide-y divide-slate-100">
        {rows.map(g => {
          const f = said.get(g.ref);
          return (
            <li
              key={g.ref}
              className="py-4 grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_20rem] gap-4 lg:gap-8"
              data-testid={`insights-guide-${g.ref}`}>
              <div className="min-w-0">
                <span className="text-sm font-black text-slate-900">{g.name}</span>
                {f && (
                  <>
                    <div className="flex flex-wrap gap-2 mt-2">
                      {f.concerns > 0 && (
                        <DrillChip
                          tone="rose"
                          testId={`drill-guide-concerns-${g.ref}`}
                          onClick={
                            drill &&
                            f.concernIds &&
                            (() =>
                              drill.ids(
                                f.concernIds,
                                `${plural(f.concerns, 'concern', 'concerns')} about ${g.name}`
                              ))
                          }>
                          {plural(f.concerns, 'concern', 'concerns')}
                        </DrillChip>
                      )}
                      {f.praise > 0 && (
                        <DrillChip
                          tone="emerald"
                          testId={`drill-guide-praise-${g.ref}`}
                          onClick={
                            drill &&
                            f.praiseIds &&
                            (() =>
                              drill.ids(
                                f.praiseIds,
                                `${plural(f.praise, 'compliment', 'compliments')} for ${g.name}`
                              ))
                          }>
                          {plural(f.praise, 'compliment', 'compliments')}
                        </DrillChip>
                      )}
                      {f.concerns === 0 && f.praise === 0 && (
                        <span className="text-xs text-slate-500">
                          No guest comments about them in this period
                        </span>
                      )}
                    </div>
                    {/* The AI summary only with a real comment behind it: with no
                        compliments or concerns it can only be guessing. */}
                    {f.summary && f.praise + f.concerns > 0 && (
                      <p className="text-sm text-slate-600 m-0 mt-2">{f.summary}</p>
                    )}
                    {f.quotes.map(q => (
                      <blockquote
                        key={q.responseId}
                        className={`m-0 mt-2 pl-3 border-0 border-l-2 border-solid text-sm text-slate-800 ${
                          q.tone === 'concern' ? 'border-rose-300' : 'border-emerald-300'
                        }`}
                        title={q.original || undefined}>
                        &ldquo;{q.text}&rdquo;
                        <span className="block text-xs text-slate-500 mt-0.5">
                          {q.score}/5{q.tourName ? `, ${q.tourName}` : ''}
                          {q.translated ? ', translated from French' : ''}
                        </span>
                      </blockquote>
                    ))}
                  </>
                )}
              </div>
              <div className="flex flex-col gap-2">
                <ScoreBar label="Overall" avg={{ average: g.averageOverall, count: g.count }} />
                <ScoreBar label="As Tour Manager" avg={g.guideRating} />
                {(g.guideQuestions || []).map(q => (
                  <div key={q.id} className="mt-1" data-testid={`guide-question-${g.ref}-${q.id}`}>
                    <div className="text-xs text-slate-500 mb-1">
                      &ldquo;{q.label}&rdquo; · {plural(q.replies, 'reply', 'replies')}
                    </div>
                    <AnswerBreakdown question={q} compact />
                  </div>
                ))}
                <div className="flex flex-wrap gap-2 mt-1">
                  <DrillChip
                    testId={`drill-guide-all-${g.ref}`}
                    onClick={
                      drill &&
                      (() =>
                        drill.filters(
                          { guideId: g.id },
                          `all ${plural(g.count, 'answer', 'answers')} from ${g.name}'s departures`
                        ))
                    }>
                    {plural(g.count, 'answer', 'answers')}
                  </DrillChip>
                  <DrillChip
                    tone={unhappyBand(g.negativeShare)}
                    testId={`drill-guide-unhappy-${g.ref}`}
                    onClick={
                      drill &&
                      (() =>
                        drill.filters(
                          { guideId: g.id, rating: 'negative' },
                          `unhappy guests from ${g.name}'s departures`
                        ))
                    }>
                    {percent(g.negativeShare)} unhappy
                  </DrillChip>
                </div>
              </div>
            </li>
          );
        })}
      </ul>
    </section>
  );
};

TourManagers.propTypes = {
  guides: PropTypes.array,
  feedback: PropTypes.array,
  drill: PropTypes.object,
  scopeLabel: PropTypes.string
};

/**
 * John (CEO), 2026-10-02: the results for every question, with replies and
 * the share of each answer, for the tour and period being looked at.
 */
const QuestionResults = ({ questions, scopeLabel }) => {
  if (!questions || questions.length === 0) return null;
  return (
    <section className={CARD} data-testid="insights-questions">
      <h2 className="text-base font-black text-slate-900 m-0 mb-1">Answers to every question</h2>
      <p className="text-xs text-slate-500 m-0 mb-4">
        {scopeLabel ? `${scopeLabel.charAt(0).toUpperCase()}${scopeLabel.slice(1)}. ` : ''}Each
        percentage is of the guests who answered that question.
      </p>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-x-10 gap-y-6">
        {questions.map(q => (
          <AnswerBreakdown key={q.id} question={q} testId={`question-${q.id}`} />
        ))}
      </div>
    </section>
  );
};

QuestionResults.propTypes = { questions: PropTypes.array, scopeLabel: PropTypes.string };

/**
 * Andy, 2026-10-02: a tour-by-tour comparison he does not have to scroll
 * sideways through, that says where its numbers come from and opens the
 * answers behind them. One card per tour, lowest overall first.
 */
const TourComparison = ({ tours, notes, average, guideQuestions, drill }) => {
  if (!tours || tours.length < 2) return null;
  const note = new Map((notes || []).map(n => [n.ref, n.summary]));
  const guideLabel =
    guideQuestions && guideQuestions.length
      ? guideQuestions.map(q => `“${q}”`).join(' and ')
      : 'the Tour Manager question';
  return (
    <section className={CARD} data-testid="insights-tours">
      <h2 className="text-base font-black text-slate-900 m-0 mb-3">Tour by tour</h2>
      <div className="grid grid-cols-1 md:grid-cols-2 2xl:grid-cols-3 gap-4">
        {tours.map(t => {
          const delta =
            average === null || average === undefined ? null : t.averageOverall - average;
          return (
            <article
              key={t.ref}
              className="rounded-2xl border border-solid border-slate-200 p-4 flex flex-col gap-3 min-w-0"
              data-testid={`insights-tour-${t.ref}`}>
              <div className="flex items-start justify-between gap-3">
                <button
                  type="button"
                  onClick={
                    drill &&
                    (() =>
                      drill.filters(
                        { tourId: t.id },
                        `all ${plural(t.count, 'answer', 'answers')} on ${t.name}`
                      ))
                  }
                  className="text-left text-sm font-black text-slate-900 bg-transparent border-0 p-0 cursor-pointer hover:underline min-w-0"
                  data-testid={`drill-tour-${t.ref}`}>
                  {t.name}
                </button>
                <div className="text-right flex-none">
                  <div
                    className={`text-2xl font-black tabular-nums leading-none ${
                      TEXT[band(t.averageOverall)]
                    }`}>
                    {t.averageOverall.toFixed(1)}
                    <span className="text-xs font-bold text-slate-400">/5</span>
                  </div>
                  {delta !== null && Math.abs(delta) >= 0.05 && (
                    <div
                      className={`text-xs font-bold mt-1 ${
                        delta > 0 ? 'text-emerald-700' : 'text-rose-700'
                      }`}>
                      {delta > 0 ? '+' : '−'}
                      {Math.abs(delta).toFixed(1)} vs all tours
                    </div>
                  )}
                </div>
              </div>
              <div className="flex flex-col gap-2">
                <ScoreBar label="Tour Manager" avg={t.guideRating} />
                <ScoreBar label="Hotels" avg={t.hotels} />
                <ScoreBar label="Experiences" avg={t.experiences} />
              </div>
              <div className="flex flex-wrap gap-2">
                <DrillChip
                  testId={`drill-tour-all-${t.ref}`}
                  onClick={
                    drill &&
                    (() =>
                      drill.filters(
                        { tourId: t.id },
                        `all ${plural(t.count, 'answer', 'answers')} on ${t.name}`
                      ))
                  }>
                  {plural(t.count, 'answer', 'answers')}
                </DrillChip>
                <DrillChip
                  tone={unhappyBand(t.negativeShare)}
                  testId={`drill-tour-unhappy-${t.ref}`}
                  onClick={
                    drill &&
                    (() =>
                      drill.filters(
                        { tourId: t.id, rating: 'negative' },
                        `unhappy guests on ${t.name}`
                      ))
                  }>
                  {percent(t.negativeShare)} unhappy
                </DrillChip>
                {t.recommendRate !== null && t.recommendRate !== undefined && (
                  <span className="text-xs font-bold rounded-lg px-2 py-1 bg-slate-100 text-slate-700">
                    {percent(t.recommendRate)} would recommend
                  </span>
                )}
              </div>
              {t.weakest && (
                <p className="text-xs text-slate-600 m-0">
                  Weakest: <span className="font-bold">{t.weakest.name}</span>{' '}
                  {t.weakest.average.toFixed(1)}/5 from{' '}
                  {plural(t.weakest.count, 'rating', 'ratings')}
                </p>
              )}
              {note.get(t.ref) && <p className="text-xs text-slate-500 m-0">{note.get(t.ref)}</p>}
            </article>
          );
        })}
      </div>
      <p className="text-xs text-slate-500 m-0 mt-4" data-testid="insights-tours-source">
        Where these come from: the big number is the average overall score guests gave. Tour Manager
        is their answers to {guideLabel}. Hotels and Experiences average every hotel and experience
        rating on that tour&rsquo;s form, leaving out &ldquo;didn&rsquo;t stay here&rdquo;. Weakest
        is the lowest-rated hotel or experience with at least 3 ratings.
      </p>
    </section>
  );
};

TourComparison.propTypes = {
  tours: PropTypes.array,
  notes: PropTypes.array,
  average: PropTypes.number,
  guideQuestions: PropTypes.array,
  drill: PropTypes.object
};

/**
 * Andy, 2026-10-02: every hotel and experience on the tour, with how many
 * guests rated it and its stars; each opens its own page of guest ratings.
 */
const TourElements = ({ elements, onOpen }) => {
  if (!elements || elements.length === 0) return null;
  const groups = [
    { kind: 'hotel', title: 'Hotels', notUsed: "didn't stay" },
    { kind: 'experience', title: 'Experiences', notUsed: "didn't take part" }
  ];
  return (
    <section className={CARD} data-testid="insights-elements">
      <h2 className="text-base font-black text-slate-900 m-0 mb-1">Hotels and experiences</h2>
      <p className="text-xs text-slate-500 m-0 mb-4">
        Every part of the tour guests were asked to rate. Open one to see each guest&rsquo;s rating.
      </p>
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        {groups.map(group => {
          const items = elements.filter(e => e.kind === group.kind);
          if (items.length === 0) return null;
          return (
            <div key={group.kind} className="min-w-0">
              <h3 className="text-xs font-bold text-slate-500 m-0 mb-2">{group.title}</h3>
              <ul className="list-none m-0 p-0 flex flex-col divide-y divide-slate-100">
                {items.map(e => (
                  <li key={`${e.kind}-${e.name}-${e.place || ''}`}>
                    <button
                      type="button"
                      onClick={onOpen && (() => onOpen(e))}
                      className="w-full text-left bg-transparent border-0 px-2 py-3 rounded-lg cursor-pointer hover:bg-slate-50 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1.5 sm:gap-4"
                      data-testid={`element-${e.kind}-${e.name}`}>
                      {/* On a phone the name gets the whole width and the
                          numbers go underneath: "Grand…" tells head office
                          nothing (2026-10-07). */}
                      <span className="min-w-0">
                        <span className="block text-sm font-bold text-slate-900 break-words sm:truncate">
                          {e.name}
                        </span>
                        <span className="block text-xs text-slate-500 break-words sm:truncate">
                          {[e.place, e.service].filter(Boolean).join(', ')}
                          {e.notUsed > 0 ? ` · ${e.notUsed} ${group.notUsed}` : ''}
                        </span>
                      </span>
                      <span className="flex items-center gap-3 flex-none">
                        <span className="text-xs text-slate-500 tabular-nums">
                          {plural(e.count, 'reply', 'replies')}
                        </span>
                        <Stars value={e.average} />
                        <span className="w-8 text-right text-sm font-black text-slate-900 tabular-nums">
                          {e.average === null ? '-' : e.average.toFixed(1)}
                        </span>
                        <span className="text-slate-400" aria-hidden="true">
                          &rsaquo;
                        </span>
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          );
        })}
      </div>
    </section>
  );
};

TourElements.propTypes = { elements: PropTypes.array, onOpen: PropTypes.func };

const Insights = ({ onDrill, onOpenElement, initialScope, onScopeChange }) => {
  const [tours, setTours] = useState([]);
  const [tourId, setTourId] = useState((initialScope && initialScope.tourId) || '');
  const [period, setPeriod] = useState((initialScope && initialScope.period) || 90);

  // Kept by the page, so coming back from an element or a drill-down lands
  // on the same tour and period.
  useEffect(() => {
    if (onScopeChange) onScopeChange({ tourId, period });
  }, [tourId, period, onScopeChange]);
  const [insight, setInsight] = useState(null);
  const [status, setStatus] = useState('loading');
  const [refreshing, setRefreshing] = useState(false);
  const [notice, setNotice] = useState('');

  useEffect(() => {
    fetchResponseFilterOptions()
      .then(options => setTours((options && options.tours) || []))
      .catch(() => setTours([]));
  }, []);

  // Only the latest request may land. A first look at a tour or period can
  // take seconds (it is generated then), so an earlier, slower request for
  // "All tours" could otherwise arrive last and show its numbers under the
  // tour now selected.
  const latest = useRef(0);

  const load = useCallback(() => {
    latest.current += 1;
    const mine = latest.current;
    setStatus('loading');
    setNotice('');
    return fetchQuestionnaireInsights({ tourId: tourId || undefined, period })
      .then(result => {
        if (mine !== latest.current) return;
        setInsight(result.insight);
        setStatus('ready');
      })
      .catch(() => {
        if (mine === latest.current) setStatus('error');
      });
  }, [tourId, period]);

  useEffect(() => {
    load();
  }, [load]);

  const refresh = () => {
    setRefreshing(true);
    setNotice('');
    const mine = latest.current;
    refreshQuestionnaireInsights({ tourId: tourId || undefined, period })
      .then(result => {
        if (mine === latest.current) setInsight(result.insight);
      })
      .catch(err => setNotice(apiErrorMessage(err)))
      .finally(() => setRefreshing(false));
  };

  const filters = (
    <div className="flex flex-wrap items-center gap-3">
      <select
        className="max-w-full min-w-0 text-sm text-slate-900 border border-solid border-slate-300 rounded-xl px-3 py-2 bg-white"
        value={tourId}
        onChange={e => setTourId(e.target.value)}
        aria-label="Tour"
        data-testid="insights-tour">
        <option value="">All tours</option>
        {tours.map(t => (
          <option key={t.id} value={t.id}>
            {t.name}
          </option>
        ))}
      </select>
      <div className="flex flex-wrap gap-1" role="radiogroup" aria-label="Period">
        {PERIODS.map(p => (
          <button
            key={p.value}
            type="button"
            role="radio"
            aria-checked={period === p.value}
            onClick={() => setPeriod(p.value)}
            data-testid={`insights-period-${p.value}`}
            className={`text-sm font-bold whitespace-nowrap rounded-xl px-3 py-2 cursor-pointer border border-solid ${
              period === p.value
                ? 'bg-slate-900 text-white border-slate-900'
                : 'bg-white text-slate-600 border-slate-300'
            }`}>
            {p.label}
          </button>
        ))}
      </div>
    </div>
  );

  if (status === 'loading') {
    return (
      <div className="flex flex-col gap-4">
        {filters}
        <div className={CARD} role="status" data-testid="insights-loading">
          <p className="text-sm text-slate-600 m-0 mb-4">
            Reading what guests said. The first time for a tour or period this can take a few
            seconds.
          </p>
          <div className="h-5 bg-slate-100 rounded w-2/3 mb-3" />
          <div className="h-4 bg-slate-100 rounded w-full mb-2" />
          <div className="h-4 bg-slate-100 rounded w-5/6" />
        </div>
      </div>
    );
  }

  if (status === 'error') {
    return (
      <div className="flex flex-col gap-4">
        {filters}
        <div className={CARD}>
          <p className="text-slate-600 m-0 mb-4">We couldn&rsquo;t load the insights.</p>
          <button
            type="button"
            onClick={load}
            className="px-4 py-2 rounded-xl bg-slate-900 text-white border-0 cursor-pointer">
            Try again
          </button>
        </div>
      </div>
    );
  }

  const { stats, narrative } = insight;

  // Every count opens the answers behind it on the Responses tab: exact ids
  // where the count came from comments, otherwise the same tour or guide over
  // the insight's own window, so the list matches the number.
  const periodLabel = (PERIODS.find(p => p.value === period) || PERIODS[1]).label.toLowerCase();
  const scopeLabel = `${
    tourId ? (tours.find(t => t.id === tourId) || {}).name || 'this tour' : 'all tours'
  }, ${periodLabel}`;
  const scope = {
    from: insight.from ? String(insight.from).slice(0, 10) : '',
    ...(tourId ? { tourId } : {})
  };
  const drill = onDrill
    ? {
        ids: (ids, label) => onDrill({ filters: { ids: ids.join(',') }, label }),
        filters: (filters, label) =>
          onDrill({ filters: { ...scope, ...filters }, label: `${label}, ${periodLabel}` })
      }
    : null;
  const canRefresh = !insight.refreshAvailableAt;

  if (stats.count === 0) {
    return (
      <div className="flex flex-col gap-4">
        {filters}
        <div className={CARD} data-testid="insights-empty">
          <p className="text-slate-600 m-0">No guest answers in this period yet.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4" data-testid="insights">
      {filters}

      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-xs text-slate-500 m-0" data-testid="insights-meta">
          Made {timeOf(insight.generatedAt)} from {plural(stats.count, 'answer', 'answers')} and{' '}
          {plural(stats.withComment, 'comment', 'comments')}
          {insight.behind ? '. New answers have come in since.' : '.'}
        </p>
        <button
          type="button"
          onClick={refresh}
          disabled={!canRefresh || refreshing}
          title={
            canRefresh
              ? 'Make these insights again from the latest answers'
              : `Can be refreshed again after ${timeOf(insight.refreshAvailableAt)}`
          }
          data-testid="insights-refresh"
          className="text-sm font-bold rounded-xl px-3 py-2 cursor-pointer border border-solid border-slate-300 bg-white text-slate-700 disabled:opacity-50 disabled:cursor-default">
          {refreshing ? 'Refreshing…' : 'Refresh'}
        </button>
      </div>
      {notice && (
        <p className="text-sm text-amber-800 bg-amber-50 rounded-xl p-3 m-0" role="alert">
          {notice}
        </p>
      )}

      {narrative ? (
        <>
          {narrative.headline && (
            <p
              className="text-lg font-black text-slate-900 m-0 max-w-[60ch]"
              data-testid="insights-headline">
              {narrative.headline}
            </p>
          )}

          {narrative.actions.length > 0 && (
            <section className={CARD} data-testid="insights-actions">
              <h2 className="text-base font-black text-slate-900 m-0 mb-3">What to act on</h2>
              <ol className="m-0 p-0 list-none flex flex-col gap-4">
                {narrative.actions.map((action, i) => (
                  <li key={action.title} className="flex gap-3">
                    <span className="flex-none w-6 h-6 rounded-full bg-slate-900 text-white text-xs font-black flex items-center justify-center">
                      {i + 1}
                    </span>
                    <div className="min-w-0">
                      <div className="text-sm font-black text-slate-900">{action.title}</div>
                      {action.why && (
                        <p className="text-sm text-slate-600 m-0 mt-1">{action.why}</p>
                      )}
                      <Evidence action={action} drill={drill} />
                    </div>
                  </li>
                ))}
              </ol>
            </section>
          )}

          <TourManagers
            guides={stats.guides}
            feedback={narrative.guides}
            drill={drill}
            scopeLabel={scopeLabel}
          />
          <TourComparison
            tours={stats.tours}
            notes={narrative.tours}
            average={stats.averageOverall}
            guideQuestions={stats.guideQuestions}
            drill={drill}
          />

          {narrative.themes.length > 0 && (
            <section className={CARD} data-testid="insights-themes">
              <h2 className="text-base font-black text-slate-900 m-0 mb-3">
                What guests talk about
              </h2>
              <ul className="list-none m-0 p-0 flex flex-col divide-y divide-slate-100">
                {narrative.themes.map(theme => (
                  <li key={theme.title} className="py-3">
                    <div className="flex items-center gap-2">
                      <span
                        className={`w-2 h-2 rounded-full ${SENTIMENT_DOT[theme.sentiment] ||
                          SENTIMENT_DOT.mixed}`}
                        aria-hidden="true"
                      />
                      <span className="text-sm font-black text-slate-900">{theme.title}</span>
                      <DrillChip
                        onClick={
                          drill &&
                          theme.responseIds &&
                          (() => drill.ids(theme.responseIds, `comments on “${theme.title}”`))
                        }>
                        {plural(theme.count, 'comment', 'comments')}
                      </DrillChip>
                    </div>
                    {theme.summary && (
                      <p className="text-sm text-slate-600 m-0 mt-1">{theme.summary}</p>
                    )}
                    {theme.quotes.map(q => (
                      <blockquote
                        key={q.responseId}
                        className="m-0 mt-2 pl-3 border-0 border-l-2 border-solid border-slate-200 text-sm text-slate-800"
                        title={q.original || undefined}>
                        &ldquo;{q.text}&rdquo;
                        <span className="block text-xs text-slate-500 mt-0.5">
                          {q.score}/5{q.tourName ? `, ${q.tourName}` : ''}
                          {q.translated ? ', translated from French' : ''}
                        </span>
                      </blockquote>
                    ))}
                  </li>
                ))}
              </ul>
            </section>
          )}

          <p className="text-xs text-slate-500 m-0">
            Themes, actions and what guests said about each Tour Manager are written by AI from the
            guests&rsquo; comments; every count and score is worked out directly from the answers.
            Read the comments on the Responses tab before acting.
          </p>
        </>
      ) : (
        <p
          className="text-sm text-slate-600 bg-slate-50 rounded-xl p-3 m-0"
          data-testid="insights-skipped">
          {SKIPPED[insight.narrativeSkipped] || SKIPPED.failed}
        </p>
      )}
      {!narrative && <TourManagers guides={stats.guides} drill={drill} scopeLabel={scopeLabel} />}
      {!narrative && (
        <TourComparison
          tours={stats.tours}
          average={stats.averageOverall}
          guideQuestions={stats.guideQuestions}
          drill={drill}
        />
      )}

      <TourElements
        elements={stats.elements}
        onOpen={
          onOpenElement &&
          (e =>
            onOpenElement({
              kind: e.kind,
              name: e.name,
              place: e.place,
              tourId: tourId || undefined,
              from: scope.from,
              scope: scopeLabel
            }))
        }
      />

      <QuestionResults questions={stats.questions} scopeLabel={scopeLabel} />

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <ScoreList
          title="Weak spots"
          lines={stats.weaknesses}
          empty="Nothing with at least 3 ratings stands out below your average."
          tone="text-rose-700"
          testId="insights-weak"
        />
        <ScoreList
          title="Strengths"
          lines={stats.strengths}
          empty="Nothing with at least 3 ratings stands out above your average."
          tone="text-emerald-700"
          testId="insights-strong"
        />
      </div>
    </div>
  );
};

/**
 * The top of the Responses tab: the latest company-wide insight's headline and
 * first actions, linking to the full tab. Shows only what already exists, so
 * it never slows the Responses tab down with a generation.
 */
export const InsightsHeadline = ({ onOpen }) => {
  const [insight, setInsight] = useState(null);

  useEffect(() => {
    fetchQuestionnaireInsights({ period: 90, ifReady: 1 })
      .then(result => setInsight(result.insight))
      .catch(() => setInsight(null));
  }, []);

  const narrative = insight && insight.narrative;
  if (!narrative || narrative.actions.length === 0) return null;

  return (
    <section
      className="rounded-2xl bg-slate-900 text-white p-5 flex flex-col gap-3"
      data-testid="insights-headline-card">
      <div className="text-xs font-bold text-slate-300">Last 90 days, all tours</div>
      {narrative.headline && <p className="text-base font-black m-0">{narrative.headline}</p>}
      <ol className="m-0 pl-5 flex flex-col gap-1 text-sm text-slate-100">
        {narrative.actions.slice(0, 3).map(a => (
          <li key={a.title}>{a.title}</li>
        ))}
      </ol>
      <button
        type="button"
        onClick={onOpen}
        data-testid="insights-open"
        className="self-start text-sm font-bold rounded-xl px-3 py-2 cursor-pointer border-0 bg-white text-slate-900">
        See all insights
      </button>
    </section>
  );
};

InsightsHeadline.propTypes = { onOpen: PropTypes.func };

Insights.propTypes = {
  onDrill: PropTypes.func,
  onOpenElement: PropTypes.func,
  initialScope: PropTypes.object,
  onScopeChange: PropTypes.func
};

export default Insights;
